import { Flags, useAuthentication, useFlags } from 'commons/hooks';

import { EntitlementName } from './entitlementName';
import { useOrganizationEntitlements } from './useOrganizationEntitlements';

export interface EntitlementPermissions {
  name: EntitlementName;
  hasEntitlementAccess: boolean;
  hasUnitsRestriction: boolean;
  isOverLimit: boolean;
  unitsAllowed?: number;
  currentUnits?: number;
  unitsOverLimit?: number;
  isLoading: boolean;
}

const defaultAccess = {
  hasEntitlementAccess: true,
  hasUnitsRestriction: false,
  isOverLimit: false,
  unitsAllowed: Number.POSITIVE_INFINITY,
  currentUnits: 0,
  unitsOverLimit: Number.NEGATIVE_INFINITY,
  isLoading: false,
};

const defaultNoAccess = {
  hasEntitlementAccess: false,
  hasUnitsRestriction: false,
  isOverLimit: false,
};

/**
 * This hook receives an entitlement name or an array of entitlement names and returns the access data for each
 *
 * useEntitlementAccess(entitlementName) => {...accessData}
 * useEntitlementAccess([]entitlementName1, entitlementName2,...]) => [{...accessData1}, {...accessData2}, ...]
 */
export const useEntitlementAccess = ((
  entitlementName: EntitlementName | EntitlementName[],
): EntitlementPermissions | EntitlementPermissions[] => {
  const { organizationId } = useAuthentication();
  const { entitlements, entitlementsUsage, isLoading, isError } = useOrganizationEntitlements(
    organizationId,
  );
  const { [Flags.ENTITLEMENTS]: withEntitlements } = useFlags();

  // if it is Candu organization or entitlements flag is off, grant access always
  if (organizationId === 1 || !withEntitlements) {
    if (typeof entitlementName === 'string') {
      return {
        name: entitlementName,
        ...defaultAccess,
      } as EntitlementPermissions;
    }
    return entitlementName.map((name) => ({
      name,
      ...defaultAccess,
    })) as EntitlementPermissions[];
  }

  // if we are still loading the data, or there was an error retrieving it, or we don't have an organization id
  // assume no access is granted as a safeguard
  if (isLoading || isError) {
    if (typeof entitlementName === 'string') {
      return {
        name: entitlementName,
        isLoading,
        ...defaultNoAccess,
      } as EntitlementPermissions;
    }
    return entitlementName.map((name) => ({
      name,
      isLoading,
      ...defaultNoAccess,
    })) as EntitlementPermissions[];
  }

  const entitlementsToCheck =
    typeof entitlementName === 'string' ? [entitlementName] : entitlementName;

  const permissions = entitlementsToCheck.reduce(
    (acc: EntitlementPermissions[], name: EntitlementName): EntitlementPermissions[] => {
      const entitlement = entitlements.find((e) => e.name === name);
      const entitlementUsage = entitlementsUsage.find((e) => e.name === name);

      // if we don't have data for the entitlement or its usage, don't grant access
      if (!entitlement || !entitlementUsage) {
        return [
          ...acc,
          {
            name,
            isLoading,
            ...defaultNoAccess,
          },
        ];
      }

      // if entitlement exists, access is granted unless overridden by isAccessGranted prop
      const isAccessGranted =
        typeof entitlement.isAccessGranted !== 'undefined' ? entitlement.isAccessGranted : true;

      const hasUnitsRestriction = 'units' in entitlement;
      // if we have a unit restriction but no limit, set allowed units to infinity
      const unitsAllowed =
        hasUnitsRestriction && entitlement.units ? entitlement.units : Number.POSITIVE_INFINITY;
      // if there is no current usage units, set to 0
      const currentUnits = entitlementUsage.units || 0;

      const isOverLimit = unitsAllowed < currentUnits;

      // if we have no limit, then we are Infinity units under limit
      // Positive number if we are over limit
      // Negative number if we are under limit
      // 0 if we are at our max allowed units
      const unitsOverLimit = Number.isFinite(unitsAllowed)
        ? unitsAllowed - currentUnits
        : Number.NEGATIVE_INFINITY;

      return [
        ...acc,
        {
          name,
          hasEntitlementAccess: isAccessGranted,
          hasUnitsRestriction: hasUnitsRestriction && Number.isFinite(unitsAllowed),
          unitsAllowed,
          currentUnits,
          unitsOverLimit,
          isOverLimit,
          isLoading,
        },
      ];
    },
    [],
  );

  if (permissions.length === 1) {
    return permissions[0] as EntitlementPermissions;
  }

  return permissions as EntitlementPermissions[];
}) as ((entitlementName: EntitlementName) => EntitlementPermissions) &
  ((entitlementName: EntitlementName[]) => EntitlementPermissions[]);
